import { removeAxiosAuth, setAxiosAuth } from "@/utils/api";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

/**
 * Sets the access and refresh tokens in cookies and updates the Axios authentication configuration.
 *
 * @param {string} accessToken - The access token to set.
 * @param {string} refreshToken - The refresh token to set.
 */
// Persist auth cookies (instead of session cookies) so the session survives
// closing the tab/browser. Matches the Cognito refresh-token validity (~30d);
// an expired access token is still recoverable via the refresh token on load.
const TOKEN_COOKIE_EXPIRES_DAYS = 30;

export const setToken = (accessToken: string, refreshToken: string) => {
  Cookies.set("admin-token", accessToken, { expires: TOKEN_COOKIE_EXPIRES_DAYS });
  Cookies.set("admin-refresh-token", refreshToken, { expires: TOKEN_COOKIE_EXPIRES_DAYS });
  setAxiosAuth();
};

/**
 * Removes the access and refresh tokens from cookies and updates the Axios authentication configuration.
 */
export const removeToken = () => {
  Cookies.remove("admin-token");
  Cookies.remove("admin-refresh-token");
  removeAxiosAuth();
};

export const getToken = (): { accessToken: string | undefined; refreshToken: string | undefined } => {
  return {
    accessToken: Cookies.get("admin-token"),
    refreshToken: Cookies.get("admin-refresh-token"),
  };
};

/**
 * Verifies if a given token is still valid based on its expiration time.
 *
 * @param {string} token - The token to verify.
 * @returns {boolean} - `true` if the token is still valid, `false` otherwise.
 */
export function verifyToken(token: string) {
  try {
    const decoded = jwtDecode<{ exp: number }>(token);
    return decoded.exp > new Date().getTime() / 1000;
  } catch (e) {
    return false;
  }
}

/**
 * Reads the stored access/refresh tokens from cookies.
 */
export const getTokens = () => ({
  accessToken: Cookies.get("admin-token"),
  refreshToken: Cookies.get("admin-refresh-token"),
});

/**
 * Decode a JWT payload WITHOUT verifying its signature.
 * Frontend port of the backend `decodeJwtPayload` (jwt-claims.util.ts).
 */
export const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const decoded = jwtDecode<Record<string, unknown>>(token);
    return decoded && typeof decoded === "object" ? decoded : null;
  } catch {
    return null;
  }
};

/**
 * Extract claim keys from a JWT into a string map.
 * Frontend port of the backend `claimExactKeys` (jwt-claims.util.ts): object/nullish
 * claims are skipped, everything else is coerced to string.
 *
 * @example
 * claimExactKeys(accessToken, ["sub", "organizationId"]) // => { sub: "...", organizationId: "..." }
 */
export const claimExactKeys = <K extends string>(token: string, keys: readonly K[]): Partial<Record<K, string>> => {
  const payload = decodeJwtPayload(token);
  if (!payload) return {};

  const claimed: Partial<Record<K, string>> = {};
  for (const key of keys) {
    const value = payload[key];
    if (value == null || typeof value === "object") continue;
    claimed[key] = String(value);
  }
  return claimed;
};
